import { useState, useEffect } from "react";
import Sidebar from "./components/Sidebar";
import DashboardHeader from "./components/DashboardHeader";
import StatsCards from "./components/StatsCards";
import JobsTable from "./components/JobsTable";
import ChartSection, { RecentActivity } from "./components/ChartSection";
import PostJobModal from "./components/PostJobModal";
import ApplicantsList from "./components/ApplicantsList";
import CompanyProfileTab from "./components/CompanyProfileTab";
import AccountManagementTab from "./components/AccountManagementTab";
import { useAuth } from "@/context/AuthContext";
import { useDashboardStats } from "@/hooks/useDashboardStats";

type DashboardSection = "overview" | "jobs" | "candidates" | "analytics" | "profile" | "account";

export default function DoanhNghiep() {
  const { user } = useAuth();
  const [showPostJob, setShowPostJob] = useState(false);
  const [activeSection, setActiveSection] = useState<DashboardSection>("overview");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { stats, monthlyStats, topJobs, recentActivities, loading, error, lastUpdated, refetch } = useDashboardStats();

  const companyName = user?.company_name || "Doanh nghiệp của bạn";

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileSidebarOpen]);

  const formatTime = (date: Date | null): string => {
    if (!date) return "";
    return date.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <div className="flex h-screen bg-background-100 overflow-hidden">
      <Sidebar
        activeKey={activeSection}
        onNavigate={(key) => setActiveSection(key as DashboardSection)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <DashboardHeader
          onPostJob={() => setShowPostJob(true)}
          onNavigate={(key) => setActiveSection(key as DashboardSection)}
          onMobileMenuToggle={() => setMobileSidebarOpen(true)}
        />

        {user?.status === "banned" && (
          <div className="bg-gradient-to-r from-red-500 via-red-600 to-rose-600 px-4 md:px-6 py-3.5 flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 flex items-center justify-center shrink-0 bg-white/20 rounded-full">
              <i className="ri-alert-fill text-xl text-white"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white">Tài khoản doanh nghiệp đã bị khóa</p>
              <p className="text-xs text-white/80 mt-0.5">
                Quản trị viên đã tạm khóa tài khoản của bạn. Bạn không thể đăng tin tuyển dụng mới. Vui lòng liên hệ quản trị viên để được hỗ trợ mở khóa.
              </p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center shrink-0">
              <i className="ri-forbid-line text-lg text-white/70"></i>
            </div>
          </div>
        )}

        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 space-y-4 md:space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-heading font-bold text-2xl text-foreground-950">
                  {activeSection === "candidates" && "Quản lý ứng viên"}
                  {activeSection === "jobs" && "Quản lý tin tuyển dụng"}
                  {activeSection === "analytics" && "Thống kê & Báo cáo"}
                  {activeSection === "profile" && "Hồ sơ doanh nghiệp"}
                  {activeSection === "account" && "Quản lý tài khoản"}
                  {activeSection === "overview" && `Xin chào, ${companyName}`}
                </h1>
                {activeSection === "overview" && lastUpdated && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground-400 bg-background-50 px-2 py-0.5 rounded-full border border-background-200/70 whitespace-nowrap">
                    <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse-badge"></span>
                    Cập nhật lúc {formatTime(lastUpdated)}
                  </span>
                )}
              </div>
              <p className="text-sm text-foreground-600">
                {activeSection === "overview" && "Đây là tổng quan hoạt động tuyển dụng của bạn trong tháng 6/2026"}
                {activeSection === "jobs" && "Quản lý tất cả tin tuyển dụng đã đăng"}
                {activeSection === "candidates" && "Xem và quản lý hồ sơ ứng viên đã ứng tuyển vào các vị trí của bạn"}
                {activeSection === "analytics" && "Thống kê chi tiết hiệu quả tuyển dụng"}
                {activeSection === "profile" && "Cập nhật thông tin doanh nghiệp hiển thị công khai trên trang hồ sơ"}
                {activeSection === "account" && "Thay đổi email và mật khẩu tài khoản doanh nghiệp"}
              </p>
            </div>

            {activeSection === "overview" && (
              <>
                <StatsCards stats={stats} loading={loading} error={error} onRetry={refetch} />
                <ChartSection monthlyStats={monthlyStats} topJobs={topJobs} loading={loading} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2">
                    <JobsTable />
                  </div>
                  <div>
                    <RecentActivity activities={recentActivities} loading={loading} />
                  </div>
                </div>
              </>
            )}

            {activeSection === "jobs" && <JobsTable />}

            {activeSection === "candidates" && <ApplicantsList />}

            {activeSection === "analytics" && (
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-10 flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-bar-chart-2-line text-2xl text-secondary-500"></i>
                </div>
                <p className="text-base font-semibold text-foreground-800">Thống kê nâng cao</p>
                <p className="text-sm text-foreground-500">Tính năng đang được phát triển. Quay lại sau nhé!</p>
              </div>
            )}

            {activeSection === "profile" && <CompanyProfileTab />}

            {activeSection === "account" && <AccountManagementTab />}
          </div>
        </main>
      </div>

      <PostJobModal isOpen={showPostJob} onClose={() => setShowPostJob(false)} />
    </div>
  );
}